import { useCallback, useEffect, useMemo, useState } from "react";
import { FileText, Search, ShieldCheck, TimerReset } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { HorizontalCardLane } from "@/components/HorizontalCardLane";
import { ApplicationDetail } from "@/components/ApplicationDetail";
import { apiClient } from "@/lib/api";

const metricCards = [
  { key: "pending", label: "Aktywne" },
  { key: "accepted", label: "Zakończone pozytywnie" },
  { key: "rejected", label: "Zakończone" },
];

export default function ReviewPage() {
  const [applications, setApplications] = useState([]);
  const [summary, setSummary] = useState({ counts: { pending: 0, accepted: 0, rejected: 0 }, recent_activity: [] });
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [submittingDecision, setSubmittingDecision] = useState(false);
  const [retryingProvisioning, setRetryingProvisioning] = useState(false);

  const loadReviewData = useCallback(async () => {
    try {
      setLoading(true);
      const [applicationsResponse, summaryResponse] = await Promise.all([
        apiClient.get("/applications", { params: { status: filter, search } }),
        apiClient.get("/dashboard/summary"),
      ]);
      setApplications(applicationsResponse.data.applications || []);
      setSummary(summaryResponse.data);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Nie udało się pobrać panelu zgłoszeń.");
    } finally {
      setLoading(false);
    }
  }, [filter, search]);

  useEffect(() => {
    loadReviewData();
  }, [loadReviewData]);

  const handleSearch = async (event) => {
    event.preventDefault();
    await loadReviewData();
  };

  const lanes = useMemo(
    () => ({
      pending: applications.filter((application) => application.status === "pending"),
      accepted: applications.filter((application) => application.status === "accepted"),
      rejected: applications.filter((application) => application.status === "rejected"),
    }),
    [applications],
  );

  const openApplication = async (application) => {
    try {
      const response = await apiClient.get(`/applications/${application._id}`);
      setSelectedApplication(response.data.application);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Nie udało się otworzyć zgłoszenia.");
    }
  };

  const handleDecision = async (decision, reviewerNote) => {
    if (!selectedApplication) return;
    setSubmittingDecision(true);
    try {
      const response = await apiClient.post(`/applications/${selectedApplication._id}/decision`, {
        decision,
        reviewer_note: reviewerNote,
      });
      setSelectedApplication(response.data.application);
      toast.success(decision === "accepted" ? "Status został zaktualizowany pozytywnie." : "Zgłoszenie zostało zamknięte.");
      await loadReviewData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Nie udało się zapisać decyzji.");
    } finally {
      setSubmittingDecision(false);
    }
  };

  const handleRetryProvisioning = async () => {
    if (!selectedApplication) return;
    setRetryingProvisioning(true);
    try {
      const response = await apiClient.post(`/applications/${selectedApplication._id}/retry-provisioning`);
      setSelectedApplication(response.data.application);
      toast.success("Ponowiono aktywację dostępu.");
      await loadReviewData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Nie udało się ponowić aktywacji.");
    } finally {
      setRetryingProvisioning(false);
    }
  };

  return (
    <div className="space-y-6">
      <section className="grid gap-4 xl:grid-cols-[1.08fr_0.92fr]">
        <Card className="surface-noise border-border bg-card shadow-[0_10px_30px_rgba(0,0,0,0.35)]">
          <CardHeader>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-xs uppercase tracking-[0.24em] text-primary/80">Byteflow | wewnętrznie</p>
                <CardTitle className="mt-2 text-3xl font-semibold tracking-tight">Panel zgłoszeń</CardTitle>
              </div>
              <div className="flex items-center gap-2 rounded-2xl border border-primary/20 bg-primary/10 px-4 py-3 text-sm text-primary">
                <ShieldCheck className="h-4 w-4" /> Dostęp aktywny
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4 text-sm text-muted-foreground">
            <p>
              Przeglądaj zgłoszenia, otwieraj pełną treść i aktualizuj status wraz z notatką wewnętrzną.
            </p>
            <form onSubmit={handleSearch} className="grid gap-3 md:grid-cols-[1fr_auto]">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Szukaj po nazwie lub ID"
                  className="pl-10"
                  data-testid="review-search-input"
                />
              </div>
              <Button type="submit" variant="secondary" data-testid="review-search-submit-button">
                <FileText className="mr-2 h-4 w-4" /> Filtruj
              </Button>
            </form>
          </CardContent>
        </Card>

        <div className="grid gap-4 sm:grid-cols-3">
          {metricCards.map((metric) => (
            <Card key={metric.key} className="border-border bg-card">
              <CardContent className="p-5">
                <p className="text-xs uppercase tracking-[0.24em] text-muted-foreground">{metric.label}</p>
                <p className="mt-3 text-3xl font-semibold">{summary.counts?.[metric.key] || 0}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      <Card className="border-border bg-card">
        <CardContent className="p-4 sm:p-5">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <Tabs value={filter} onValueChange={setFilter} className="w-full lg:w-auto">
              <TabsList>
                <TabsTrigger value="all" data-testid="review-filter-all">Wszystkie</TabsTrigger>
                <TabsTrigger value="pending" data-testid="review-filter-pending">Aktywne</TabsTrigger>
                <TabsTrigger value="accepted" data-testid="review-filter-accepted">Pozytywne</TabsTrigger>
                <TabsTrigger value="rejected" data-testid="review-filter-rejected">Zamknięte</TabsTrigger>
              </TabsList>
            </Tabs>
            <Button variant="outline" onClick={loadReviewData} data-testid="review-refresh-button">
              <TimerReset className="mr-2 h-4 w-4" /> Odśwież listę
            </Button>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="space-y-4">
          <div className="h-48 rounded-3xl bg-muted" />
          <div className="h-48 rounded-3xl bg-muted" />
        </div>
      ) : (
        <div className="space-y-6">
          <HorizontalCardLane
            title="Aktywne zgłoszenia"
            items={lanes.pending}
            onOpen={openApplication}
            emptyCopy="Brak aktywnych zgłoszeń do obsługi."
            testId="review-lane-pending"
          />
          <HorizontalCardLane
            title="Zakończone pozytywnie"
            items={lanes.accepted}
            onOpen={openApplication}
            emptyCopy="Brak pozytywnie zakończonych zgłoszeń przy obecnym filtrze."
            testId="review-lane-approved"
          />
          <HorizontalCardLane
            title="Zamknięte zgłoszenia"
            items={lanes.rejected}
            onOpen={openApplication}
            emptyCopy="Brak zamkniętych zgłoszeń przy obecnym filtrze."
            testId="review-lane-rejected"
          />
        </div>
      )}

      <ApplicationDetail
        application={selectedApplication}
        open={Boolean(selectedApplication)}
        onOpenChange={(open) => {
          if (!open) setSelectedApplication(null);
        }}
        onDecision={handleDecision}
        submitting={submittingDecision}
        onRetryProvisioning={handleRetryProvisioning}
        retryingProvisioning={retryingProvisioning}
      />
    </div>
  );
}
